import type { PlanDetailsCopy } from '../planDetails'
import { ruPlural } from './plural'

const ru: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Ваш возраст',
    retirement_age: 'Выход на пенсию',
    annual_income: 'Годовой доход',
    annual_savings: 'Годовые сбережения',
    inflation: 'Инфляция',
    realization_tax_rate: 'Ставка налога на прирост капитала',
    cost_basis: 'Стоимость приобретения',
    w0: 'Инвестируемые активы сегодня',
    primary_residence: 'Ваше жильё',
    total_investable: 'Инвестируемые активы',
    expected_return: 'Ожидаемая доходность',
    volatility: 'Волатильность',
    horizon: 'Горизонт планирования',
    jurisdiction: 'Налоговое резидентство',
    risk_aversion: 'Толерантность к риску',
    birth_year: 'Год рождения',
    birth_date: 'Дата рождения',
    marital_status: 'Семейное положение',
    dependents: 'Иждивенцы',
    dependent_ages: 'Возраст иждивенцев',
    children: 'Дети',
    children_birth_years: 'Годы рождения детей',
    partner_age: 'Возраст партнёра',
    partner_annual_income: 'Годовой доход партнёра',
    partner_retirement_age: 'Пенсионный возраст партнёра',
    plan_to_age: 'Возраст окончания плана',
    gross_annual_income: 'Годовой доход до налогов',
    net_income: 'Чистый доход',
    annual_spending: 'Годовые расходы',
    essential_spending: 'Обязательные расходы',
    flexible_spending: 'Гибкие расходы',
    investments_total: 'Всего инвестировано',
    total_investable_wealth: 'Инвестируемые активы, всего',
    portfolio_mix: 'Структура портфеля',
    cgt_rate: 'Ставка налога на доход от капитала',
    interest_rate: 'Процентная ставка',
    dividend_yield: 'Дивидендная доходность',
    wealth_tax_rate: 'Ставка налога на имущество',
    filing_jurisdictions: 'Страны подачи деклараций',
    citizenship: 'Гражданство',
    tax_residency: 'Заявленное налоговое резидентство',
    state: 'Штат или провинция',
    region: 'Регион',
    city: 'Город',
    locality: 'Населённый пункт',
    church_tax_applicable: 'Плательщик церковного налога',
    life_cover: 'Страхование жизни',
    income_protection: 'Страхование дохода',
    employer_match: 'Взнос работодателя',
    pension: 'Пенсия',
    state_pension: 'Государственная пенсия',
    social_security: 'Социальное обеспечение',
    wage_growth: 'Рост заработной платы',
    stability: 'Стабильность дохода',
    has_will: 'Завещание составлено',
    risk_attitude: 'Отношение к риску',
  },
  keyLabels: {
    goal_priority: 'Порядок целей',
    current_age: 'Возраст',
    inflation: 'Допущение по инфляции',
    jurisdiction: 'Налоговое резидентство',
    realization_tax_rate: 'Налог при продаже для финансирования цели',
    citizenship: 'Гражданство',
    total_investable: 'Инвестируемые активы',
    annual_income: 'Годовой доход',
    annual_savings: 'Годовые сбережения',
    risk_tier: 'Уровень риска',
    retirement_age: 'Пенсионный возраст',
    target_wealth: 'Целевой капитал',
    primary_residence_sellable: 'Продажа жилья / переезд в меньшее',
  },
  channelLabels: {
    web_levers: 'Рычаги на сайте',
    web_cockpit: 'Панель решений',
    web: 'Сайт',
    chat: 'ИИ-ассистент',
    mcp: 'ИИ-ассистент',
    full_review: 'Полная проверка',
    full_plan: 'Полный план',
    what_if: 'Сценарный анализ',
  },
  objectiveLabels: {
    status_quo: 'Текущее положение',
    max_goal_probability: 'Максимальная вероятность успеха',
    lowest_drawdown: 'Минимальная просадка',
    highest_growth: 'Максимальный рост',
    fastest_to_goal: 'Самое раннее достижение цели',
  },
  choices: {
    interest_only: 'Только проценты',
    fixed_rate_amortizing: 'С погашением долга (фиксированная ставка)',
    variable_rate_amortizing: 'С погашением долга (плавающая ставка)',
    repayment: 'С погашением долга',
    refinance: 'Рефинансировать по рыночной ставке',
    repay: 'Погасить полностью',
  },
  values: {
    yearsShort: (n) => `${n} г.`,
    shareAbout: (pct) => `около ${pct} его стоимости`,
    sharePart: 'часть его стоимости',
    aroundAge: (age) => ` примерно в ${age} лет`,
    inAboutYears: (n) => ` примерно через ${n} г.`,
    maySellFor: (share, when) => `Может быть продано, чтобы высвободить ${share}${when}, только если это улучшает план.`,
    maySell: 'Может быть продано, если это улучшает план.',
    structuredFact: 'Структурированные данные',
  },
  ask: { talkThrough: 'Обсудить с MyCIO' },
  review: {
    openInputCheckFailed: 'Не удалось завершить проверку недостающих данных. Статус проверки — ниже.',
    finishedLoading: 'Ваша проверка завершена. Загружаем результаты…',
    draftReady: 'Предварительная проверка готова — ниже выполненный анализ, то, что не удалось рассчитать, и вопросы, ответы на которые позволят завершить расчёт.',
    finishedNotLoaded: 'Ваша проверка завершена, но результаты пока не удалось загрузить. Повторяем попытку — расчёт заново выполняться не будет.',
    fetchFailed: 'Ваша проверка завершена, но сейчас её не удалось получить. Она сохранена — попробуйте ещё раз чуть позже; пересчитывать ничего не нужно.',
    notComputed: (list) => `Не рассчитано в этом запуске: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 изменение не сохранено' : `${f} ${ruPlural(n, 'изменение не сохранено', 'изменения не сохранены', 'изменений не сохранено')}`} — см. раздел «Ваши данные».`,
  },
  facts: {
    chooseOption: 'Выберите вариант.',
    couldNotSave: 'Не удалось сохранить значение. Ваш план не изменился.',
    couldNotUndo: 'Не удалось отменить изменение.',
    choosePlaceholder: 'Выберите…',
    confirmedBadge: 'Подтверждено — изменить можно через чат',
    confirmedTitle: (was) => `Вы уже подтвердили это значение: ${was}. Попросите MyCIO в чате заменить его.`,
  },
  assumptionsToConfirm: { anInput: 'исходные данные', assumedSuffix: (v) => ` — допущение: ${v}` },
  editor: {
    enterNumber: 'Введите число.',
    enterNumberFor: (l) => `Введите число для поля «${l}».`,
    enterPercentFor: (l) => `Введите процент для поля «${l}».`,
    chooseYesNoFor: (l) => `Выберите «Да» или «Нет» для поля «${l}».`,
    semicolonBetween: (l) => `Разделяйте элементы поля «${l}» знаком «;».`,
    useNumbersFor: (l) => `Используйте числа для поля «${l}».`,
    wholeNumbersFor: (l) => `Используйте целые числа для поля «${l}», разделяя их знаком «;».`,
    commaSeparated: 'через запятую',
  },
  freshnessUnknown: 'Не удалось проверить, учитывает ли сохранённый расчёт последние изменения по вашим счетам. Обновите страницу и проверьте счета, прежде чем опираться на эти цифры.',
  goals: { goalN: (n) => `Цель ${n}`, whatMoves: 'что на это влияет →', earliest: (y) => `самое раннее ≈ ${y}`, rank: 'Приоритет' },
  issues: {
    mainConstraint: 'Главное ограничение этого плана',
    riskToReview: 'Риск для проверки',
    missingInformation: 'Недостающие данные',
    whyItMatters: (t) => `Почему это важно: ${t}`,
    seeEvidence: 'Смотреть обоснование ↓',
    noneFound: 'Выполненный анализ не выявил существенных проблем на основе данных, которые сейчас есть в плане.',
    notAvailable: 'Анализ проблем пока недоступен. Пустой раздел не означает, что проблем нет.',
    discussQuestion: 'Разберите проблемы, выявленные в этом плане. Объясните, на чём они основаны, и спросите, могут ли недостающие сведения о моей ситуации изменить их приоритет или вывод.',
    discussLabel: 'Обсудить эти проблемы',
  },
  policies: {
    optionTested: 'Проверенный вариант',
    computedEffect: (v) => `Рассчитанный эффект: ${v}`,
    tradeoff: (v) => `Компромисс: ${v}`,
    backToAction: 'Назад к действию ↑',
    tradeoffsHeading: 'Компромиссы по проверенным вариантам',
  },
  transfer: {
    toHeirs: (m) => `+${m} наследникам`,
    fundedPoints: (label, pts) => `${label} ${pts} п. п.`,
    noChange: ' · без изменений в финансировании ваших целей',
  },
  tax: {
    rulesIntro: 'Показаны только нормы, привязанные к этому расчёту. Для каждой записи указаны зафиксированные сведения об источнике; отсутствующие даты или данные о сфере применения так и отмечены как отсутствующие.',
    title: 'Налоговый режим, применённый в этом плане',
    countryBookPrimary: 'Прогноз год за годом применял налоговое законодательство соответствующей страны. Источники, явно отмеченные как официальные или первичные, перечислены ниже.',
    countryBookNoPrimary: 'Прогноз год за годом применял налоговое законодательство соответствующей страны. К этому сохранённому результату пока не приложены ссылки на первичные источники, поэтому перепроверьте решения с налоговыми последствиями, прежде чем действовать.',
    simplified: 'В расчёте использован упрощённый налоговый режим. Прежде чем опираться на сравнения с налоговыми последствиями, ознакомьтесь с ограничениями ниже.',
    askQuestion: 'Объясните, какие налоговые законы и какие национальные уровни применены к этому плану, какие учтены приближённо или исключены и какие обстоятельства моей налоговой ситуации могут изменить результат. Используйте только первичные источники, действительно привязанные к этому расчёту, и назовите недостающие.',
    askLabel: 'Спросить о налоговом режиме',
  },
  inputs: { title: 'Данные, которые могут изменить результат', infoToConfirm: 'Данные для подтверждения' },
  gated: {
    countChoices: (n, f) => `${f} ${ruPlural(n, 'вариант, рассчитанный', 'варианта, рассчитанных', 'вариантов, рассчитанных')} для вас`,
    mappedYears: (n, f) => `мы рассчитали ${ruPlural(n, 'следующий', 'следующие', 'следующие')} ${f} ${ruPlural(n, 'год', 'года', 'лет')}`,
    extraChecks: (n, f) => `${f} ${ruPlural(n, 'дополнительная проверка', 'дополнительные проверки', 'дополнительных проверок')} вашего плана`,
    placeholderRows: ['Иллюстративная строка', 'Пример значения', 'Пример варианта', 'Заполнитель'],
  },
}

export default ru
